/*
 * COD fee toggle controller (cart page).
 *
 * Keeps two things in sync when the customer picks a payment method:
 *   1. a hidden "taxă ramburs" fee line item, and
 *   2. a cart attribute ("Metodă de plată") recording the choice.
 *
 * Both are set in one atomic /cart/update.js call. The fee quantity is set
 * absolutely (0 or 1), so it can never stack from double clicks or two tabs.
 * Shopify's Section Rendering API updates the cart items, totals and cart
 * bubble in place. No popup, no redirect.
 *
 * Self-heal: on every cart view the controller reconciles the fee line to the
 * stored attribute. If the attribute says "ramburs" but the fee line is gone
 * (customer deleted it to dodge the charge), the fee is re-added. If it says
 * "card" but a fee line is present, the fee is removed. A cart holding only
 * the fee is emptied of it (orphan guard).
 *
 * Enforcement: the checkout button is blocked until a method is chosen. This
 * fails OPEN: if this script fails to load, checkout still works, so a sale is
 * never lost, we only lose enforcement in that rare case.
 *
 * Config comes from the [data-cod-fee] fieldset rendered by
 * snippets/cod-payment-method.liquid, which lives inside main-cart-footer's
 * .js-contents and is re-rendered on every cart update. Listeners are
 * delegated so they survive that.
 */
(function () {
  const CONFIG_SELECTOR = '[data-cod-fee]';
  let busy = false;
  // Client-side memory of the last choice, so a fast checkout click right
  // after picking a radio (before the server round-trip finishes) is allowed.
  let pendingMethod = '';

  function config() {
    const el = document.querySelector(CONFIG_SELECTOR);
    if (!el) return null;
    return {
      el: el,
      variantId: el.dataset.variantId,
      feeInCart: el.dataset.feeInCart === 'true',
      orphan: el.dataset.orphan === 'true',
      method: el.dataset.method || '',
      attrKey: el.dataset.attrKey,
      methodCard: el.dataset.methodCard,
      methodCod: el.dataset.methodCod,
    };
  }

  function sectionsToRender() {
    const list = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items) list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    return list;
  }

  function innerHTMLFrom(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function applySections(sections) {
    if (!sections) return;
    sectionsToRender().forEach((s) => {
      const html = sections[s.section];
      if (!html) return;
      const container = document.getElementById(s.id);
      if (!container) return;
      const target = container.querySelector(s.selector) || container;
      const inner = innerHTMLFrom(html, s.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  function setBusy(state) {
    busy = state;
    const el = document.querySelector(CONFIG_SELECTOR);
    if (el) el.classList.toggle('cod-method--busy', state);
    const items = document.getElementById('main-cart-items');
    if (items) items.classList.toggle('cart__items--disabled', state);
  }

  function showError(message) {
    const el = document.querySelector('[data-cod-error]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('cod-method__error--shown');
  }

  function clearError() {
    const el = document.querySelector('[data-cod-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.remove('cod-method__error--shown');
  }

  // Blocking modal shown when the customer tries to check out with no method
  // chosen. Built once and reused; lives on <body> so cart re-renders never
  // wipe it. Styling comes from snippets/cod-payment-method.liquid.
  let modalEl = null;
  function buildModal() {
    if (modalEl) return modalEl;
    const overlay = document.createElement('div');
    overlay.className = 'cod-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'cod-modal-title');
    overlay.innerHTML =
      '<div class="cod-modal__dialog">' +
      '<h2 class="cod-modal__title" id="cod-modal-title">Alege metoda de plată</h2>' +
      '<p class="cod-modal__text">Selectează plată card online sau plată la livrare pentru a continua spre finalizarea comenzii.</p>' +
      '<button type="button" class="cod-modal__button" data-cod-modal-close>Am înțeles</button>' +
      '</div>';
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target.closest('[data-cod-modal-close]')) {
        closeModal();
      }
    });
    document.body.appendChild(overlay);
    modalEl = overlay;
    return overlay;
  }

  function openModal() {
    const overlay = buildModal();
    overlay.classList.add('cod-modal--open');
    const btn = overlay.querySelector('[data-cod-modal-close]');
    if (btn) btn.focus();
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.classList.remove('cod-modal--open');
    const c = config();
    if (c && c.el) c.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modalEl && modalEl.classList.contains('cod-modal--open')) {
      closeModal();
    }
  });

  function requestBody(payload) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    };
  }

  // One atomic call: set the attribute AND the fee quantity together.
  function applyChoice(wantFee) {
    if (busy) return;
    const c = config();
    if (!c || !c.variantId) return;
    const method = wantFee ? c.methodCod : c.methodCard;
    // Already exactly in the desired state? Nothing to do.
    if (wantFee === c.feeInCart && c.method === method) return;

    pendingMethod = method;
    clearError();
    setBusy(true);

    const attributes = {};
    attributes[c.attrKey] = method;
    const updates = {};
    updates[c.variantId] = wantFee ? 1 : 0;

    fetch(window.routes.cart_update_url, requestBody({
      attributes: attributes,
      updates: updates,
      sections: sectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    }))
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then((res) => {
        if (!res.ok) {
          showError('Nu am putut actualiza metoda de plată. Reîncearcă.');
          return;
        }
        applySections(res.data.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cod-fee', cartData: res.data });
        }
      })
      .catch(() => {
        showError('Nu am putut actualiza metoda de plată. Reîncearcă.');
      })
      .finally(() => {
        setBusy(false);
      });
  }

  // Bring the fee line back in line with the stored choice.
  function reconcile() {
    const c = config();
    if (!c || busy) return;
    if (c.orphan) { applyChoice(false); return; }           // only the fee left
    if (c.method === c.methodCod && !c.feeInCart) { applyChoice(true); return; }  // chose COD, fee missing -> restore
    if (c.method === c.methodCard && c.feeInCart) { applyChoice(false); return; } // chose card, stray fee -> drop
  }

  function hasChoice() {
    const c = config();
    if (!c) return true; // no chooser present -> do not block anything
    if (pendingMethod) return true;
    return c.method === c.methodCod || c.method === c.methodCard || c.feeInCart;
  }

  function enforceChoice(event) {
    if (hasChoice()) return;
    event.preventDefault();
    event.stopPropagation();
    showError('Selectează metoda de plată pentru a continua.');
    openModal();
  }

  // Radio change -> apply choice.
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cod-choice]');
    if (!input || !input.checked) return;
    applyChoice(input.value === 'cod');
  });

  // Block the checkout button (click) until a method is chosen.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('#checkout, [name="checkout"]');
    if (btn) enforceChoice(event);
  });

  // Block form submit via Enter, only when the checkout button is the submitter.
  document.addEventListener('submit', (event) => {
    const s = event.submitter;
    if (s && (s.id === 'checkout' || s.name === 'checkout')) enforceChoice(event);
  }, true);

  // Re-run the orphan/sync check after any cart update (but not our own).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cod-fee') return; // avoid recursion
      setTimeout(reconcile, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
